import { buildWorkflowPrompt } from './mailWorkflows';
// ============================================================
// sessionContext — アクティブなセッションの文脈（S3・2026-08-26）
//
// 正: booking-platform/md/ai-control-center.md §16
//
// 予約セッションを開いている間、「この予約」の解決とシステムプロンプトの
// 文脈をここが供給する。pageContext（画面の選択）より優先される。
// ============================================================

export interface SessionMeta {
  kind: 'work' | 'reservation';
  /** reservation のとき contact_code */
  anchor?: string;
  /** 表示名（例: 予約 R0K1QF8O 山崎 信行 様） */
  label?: string;
  /** 起点メールの種別（mailWorkflows の key）。メール起点で開いたときだけ入る */
  workflow?: string;
}

let current: SessionMeta = { kind: 'work' };

export function setSessionMeta(meta: SessionMeta): void {
  current = meta;
}

export function getSessionMeta(): SessionMeta {
  return current;
}

/**
 * 予約セッションへの誘導ルール。**業務セッション中だけ**注入する。
 * ⚠ 「既にセッション内なら勧めない」という否定指示は Gemini が無視することがある
 *   （2026-08-28 実運用: セッション内で「開きますか？」と勧め、オペレータが混乱 →
 *    再オープンでタイムライン注入が重複）。指示で禁じるのではなく、
 *   予約セッション中は**ルール自体をプロンプトから消す**（構造的対策）。
 */
export function buildSessionGuidancePrompt(): string {
  if (current.kind === 'reservation') return '';
  return [
    '# 予約セッションへの誘導',
    '',
    '**特定の予約を1件参照して答えたら、最後に必ず一言で勧める**:',
    '「この予約の作業を続ける場合は予約セッションを開けます。開きますか？」',
    '- ユーザーが「はい/開いて」と言ったら open_reservation_session を呼ぶ',
    '- **勝手に開かない**（見るだけの用件も多い）。勧めるのは1会話につき同じ予約で1回まで',
    '- 一覧（複数件）を出しただけのときは勧めない',
  ].join('\n');
}

/** システムプロンプトに注入するブロック。業務セッション中は空文字 */
export function buildSessionContextPrompt(): string {
  if (current.kind !== 'reservation' || !current.anchor) return '';
  return [
    '# 現在のセッション',
    `- いまは**予約セッション**を開いている: ${current.label || current.anchor}（予約番号 ${current.anchor}）`,
    '- 「この予約」「今回の予約」は常にこの予約番号を指す。画面の選択より優先する。',
    '- **開いた時点の予約データ（お客様名・日時・車両・ステータス・料金）は文脈に注入済み**。'
      + '同じことを聞かれても search_reservations を呼び直さず、注入された値を使う'
      + '（変更を加えた直後など、最新値が必要なときだけ呼び直す）。',
    `- **別の予約番号（${current.anchor} 以外）の作業**（変更・メール・キャンセル等）を頼まれたら、`
      + '作業を始める前に open_reservation_session でその予約のセッションへ切り替える。'
      + 'このセッションのまま別予約の作業をすると、記録が別予約の履歴に混ざってしまう。'
      + '参照だけ（料金や空きを見るだけ）なら切り替え不要。'
      + '**お客様へのメール返信は、そのメールが届いたこのセッションで起案してよい**（本文に別予約の番号があっても切り替えない）。'
      + 'スタッフが「このセッションに戻ります」と戻ってきたら、同じ予約へ切り替え直さない（2026-10-15）。',
    '- この予約に関係ない一般作業を頼まれたら、業務セッションに戻るか確認する。',
    ...(current.workflow ? ['', buildWorkflowPrompt(current.workflow)] : []),
  ].join('\n');
}
